import { Box, Grid, Typography } from "@mui/material";
import theme from "Theme/AppTheme";
import AcxCard from "components/UI/Cards/AcxCard";
import { observer } from "mobx-react";
import React from "react";
import { defaultShadowsForSignals } from "components/Signals/SignalsUtils";
import UniqueCardStore from "./UniqueCardStore";
import { CARD_CONTENT_ATTRIBUTE } from "../../Dashboard/Layout/grid";
import { useCurrentDashboard } from "components/Signals/useCurrentDashboard";
import {
    AcxDeprecatedTag,
    useStatusTagVisible,
} from "components/UI/StatusTags";
import {
    ISignalsTopicsOnTheRiseResponse,
    ITopicOnTheRise,
} from "components/Signals/SignalsModels";
import { SignalsTrendingIndicator } from "../SignalsTrendingIndicator";
import PointMenu from "../Drilldown/PointMenu";
import DrilldownDialog from "../Drilldown/DrilldownDialog";
import UniqueCardMenu from "./UniqueCardMenu";
import { ObjectFilterIndicator } from "../ObjectFilters";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import { resolveDataAvailability } from "components/Signals/DataAvailability/DataAvailability";
import DataAvailabilityInfo from "components/Signals/DataAvailability/DataAvailabilityInfo";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";

const cardRootStyle: React.CSSProperties = {
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: defaultShadowsForSignals,
};

const placeholderTopics: ITopicOnTheRise[] = Array.from({ length: 4 }).map(
    () => ({
        topicName: " ",
        percentChange: 0,
        volume: 0,
        isNew: false,
        topicNumber: 0,
        themeNumber: 0,
    }),
);

const TopicsOnTheRise = observer(
    ({
        cardStore,
    }: {
        cardStore: UniqueCardStore<ISignalsTopicsOnTheRiseResponse>;
    }) => {
        const authStore = useStore(AuthStore);
        const currentDashboard = useCurrentDashboard();
        const [pointMenuOpen, setPointMenuOpen] = React.useState(false);
        const [pointMenuAnchor, setPointMenuAnchor] = React.useState<
            { top: number; left: number } | undefined
        >(undefined);

        const isLoading = cardStore.isRefreshing;

        function handleOnClick(
            event: React.MouseEvent<HTMLDivElement>,
            topicData: ITopicOnTheRise,
        ) {
            if (
                cardStore.isPresentationMode ||
                isLoading ||
                !cardStore.isPointMenuEnabled ||
                !cardStore.drilldownStore
            )
                return;

            cardStore.drilldownStore.clearSelection();
            cardStore.drilldownStore.selectTopic({
                selectedTopic: topicData.topicName,
                topicNumber: topicData.topicNumber,
                themeNumber: topicData.themeNumber,
                x: topicData.topicName,
                count:
                    typeof topicData.volume === "number"
                        ? topicData.volume
                        : Number(topicData.volume) || null,
            });
            setPointMenuAnchor({
                top: event.clientY,
                left: event.clientX,
            });
            setPointMenuOpen(true);
        }

        const handlePointMenuClose = () => {
            setPointMenuOpen(false);
        };

        const hasData = (cardStore.data?.data.length ?? 0) > 0;
        const topicsToRender =
            isLoading || hasData
                ? hasData && !isLoading
                    ? cardStore.data?.data ?? []
                    : cardStore.data?.data?.length
                    ? cardStore.data.data
                    : placeholderTopics
                : [];
        const shouldShowNoData = !isLoading && topicsToRender.length === 0;
        const dataAvailability = resolveDataAvailability({
            authStore,
            policy: cardStore.config.dataAvailability,
            defaultEmptyStateVariant:
                cardStore.config.renderOptions.emptyStateVariant,
            startDate: cardStore.state?.startDate,
            endDate: cardStore.state?.endDate,
            hasRenderableData: hasData,
        });
        const showDataAvailabilityInfo =
            !!dataAvailability.infoKind &&
            !isLoading &&
            !cardStore.error &&
            !cardStore.isPresentationMode &&
            !currentDashboard?.isExportCaptureInProgress;
        const showMenuButton =
            !cardStore.isPresentationMode &&
            cardStore.config.renderOptions.menu;
        const cardTitle = cardStore.title || "Topics on the Rise";
        const cardSubtitle = cardStore.subtitle;
        // Presentation mode is a clean read-only view, so the deprecation marker is suppressed
        // there. `.no-export` additionally strips it from the per-chart PNG export.
        // Called unconditionally: the tag may declare a permission, and hooks cannot be
        // called behind the Deprecated/presentation-mode checks below.
        const isStatusTagVisible = useStatusTagVisible(cardStore.config.status);
        const deprecation =
            isStatusTagVisible &&
            cardStore.config.status?.tag === "Deprecated" &&
            !currentDashboard?.isPresentationMode
                ? cardStore.config.status
                : undefined;

        return (
            <Grid sx={{ height: "100%" }}>
                <Grid
                    item
                    container
                    xs={12}
                    // Fills the card, so the layout measures its rows instead.
                    {...{ [CARD_CONTENT_ATTRIBUTE]: "" }}
                    sx={{
                        paddingLeft: theme.spacing(2),
                        paddingRight: theme.spacing(2),
                        paddingBottom: theme.spacing(2),
                        backgroundColor: theme.palette.primary[800],
                        borderRadius: 2,
                        height: "100%",
                        alignContent: "flex-start",
                    }}
                >
                    <Grid
                        item
                        container
                        xs={12}
                        sx={{
                            paddingLeft: theme.spacing(1),
                            paddingTop: theme.spacing(2),
                            paddingBottom: theme.spacing(2),
                        }}
                        gap={1}
                        alignItems={"center"}
                        justifyContent={
                            showMenuButton ? "space-between" : "flex-start"
                        }
                    >
                        <Grid item xs>
                            <Grid container alignItems="center" gap={1}>
                                <Typography
                                    sx={{
                                        color: theme.palette.white.main,
                                        fontWeight: 600,
                                        fontSize: "16px",
                                        lineHeight: "24px",
                                    }}
                                >
                                    {cardTitle}
                                </Typography>
                                {deprecation && (
                                    <Grid item className="no-export">
                                        <AcxDeprecatedTag
                                            infoText={deprecation.infoText}
                                        />
                                    </Grid>
                                )}
                                {showDataAvailabilityInfo &&
                                    dataAvailability.infoKind && (
                                        <Grid item className="no-export">
                                            <DataAvailabilityInfo
                                                kind={
                                                    dataAvailability.infoKind
                                                }
                                                color={
                                                    theme.palette.white.main
                                                }
                                            />
                                        </Grid>
                                    )}
                            </Grid>
                            {!!cardSubtitle && (
                                <Typography
                                    sx={{
                                        color: theme.palette.white.main,
                                        opacity: 0.9,
                                        fontSize: "13px",
                                        lineHeight: "18px",
                                    }}
                                >
                                    {cardSubtitle}
                                </Typography>
                            )}
                        </Grid>
                        {showMenuButton && (
                            <Grid
                                item
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <ObjectFilterIndicator store={cardStore} />
                                <UniqueCardMenu
                                    store={cardStore}
                                    iconButtonStyle={{
                                        color: theme.palette.white.main,
                                        border: "none",
                                        backgroundColor: "transparent",
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                    {!shouldShowNoData ? (
                        <Box
                            sx={{
                                width: "100%",
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(240px, 1fr))",
                                gap: theme.spacing(2),
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            {topicsToRender.map((i, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        zIndex: 1000, // keep spinner below header
                                        "&:hover":
                                            !cardStore.isPresentationMode &&
                                            !isLoading
                                                ? { cursor: "pointer" }
                                                : undefined,
                                    }}
                                    onClick={(e) => handleOnClick(e, i)}
                                    data-testid="topics-on-the-rise-item"
                                >
                                    <AcxCard
                                        loading={isLoading}
                                        loadingSize={24}
                                        title={
                                            <Typography
                                                fontWeight="600"
                                                fontSize="16px"
                                                color={theme.palette.gray[500]}
                                                lineHeight="1.5"
                                            >
                                                {isLoading ? " " : i.topicName}
                                            </Typography>
                                        }
                                        rootStyle={cardRootStyle}
                                        headerStyle={{ paddingBottom: 0 }}
                                        mainContentStyle={{
                                            justifyContent: "start",
                                            paddingTop: 0,
                                            paddingBottom: theme.spacing(2),
                                        }}
                                        mainContent={
                                            <Grid
                                                container
                                                item
                                                xs={12}
                                                justifyContent={"space-between"}
                                                alignItems="center"
                                            >
                                                <Typography
                                                    fontWeight="600"
                                                    fontSize="20px"
                                                    lineHeight="32px"
                                                    color={
                                                        theme.palette.gray[600]
                                                    }
                                                    data-testid="topics-item-volume"
                                                >
                                                    {i.volume}
                                                </Typography>
                                                <Grid container item xs={10}>
                                                    <SignalsTrendingIndicator
                                                        inputValue={
                                                            i.percentChange
                                                        }
                                                        isNew={i.isNew}
                                                        showGrowthSubtitle
                                                        isNegativeRed
                                                    />
                                                </Grid>
                                            </Grid>
                                        }
                                    />
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Grid
                            item
                            container
                            xs={12}
                            style={{ color: theme.palette.white.main }}
                        >
                            <AcxEmptyState
                                size="small"
                                variant={dataAvailability.emptyStateVariant}
                            />
                        </Grid>
                    )}
                </Grid>
                <PointMenu
                    store={cardStore}
                    open={pointMenuOpen}
                    anchorPosition={pointMenuAnchor}
                    onClose={handlePointMenuClose}
                />
                <DrilldownDialog
                    baseObjectStore={cardStore}
                    drilldownStore={cardStore.drilldownStore}
                />
            </Grid>
        );
    },
);

export default TopicsOnTheRise;
